import { fetchApi } from '@/lib/api';
import type { TaskSearchHit } from '@shared/types/tasks';
import type { PolicySearchHit } from '@shared/types/policies';

export type ProductSearchItem = {
  variant_id: string;
  code: string | null;
  barcode: string | null;
  displayname: string | null;
  variant_title: string | null;
  xero_status?: string | null;
  unitprice: string | number | null;
  price: string | number | null;
  /** Shopify compare-at (was) from latest variant_price_daily; 0/null = unset. */
  compare_at_price?: string | number | null;
  quantityonhand: string | number | null;
  product_id?: string | null;
  product_title: string | null;
  /** Shopify Online Store URL for the parent product (null if unpublished). */
  onlinestoreurl?: string | null;
  /** Stable shortcode for /r/:code website QR redirects. */
  website_shortcode?: string | null;
  /** Absolute short URL from ensure API (preferred over building from shortcode). */
  website_short_url?: string | null;
  vendor_name: string | null;
  /** Purchase-order item search only: the Xero item's purchase price and description. */
  purchase_unit_price?: string | number | null;
  purchase_description?: string | null;
};

export type WebsiteShortcodeResult = {
  variant_id: string;
  short_url: string | null;
  onlinestoreurl: string | null;
  website_shortcode: string | null;
};

/** Ensure stable website shortcodes for label QR (Website) printing. */
export async function ensureWebsiteShortcodes(
  variantIds: string[]
): Promise<WebsiteShortcodeResult[]> {
  const ids = [...new Set(variantIds.filter(Boolean))];
  if (ids.length === 0) return [];
  const res = await fetchApi<{ results: WebsiteShortcodeResult[] }>(
    '/products/website-shortcodes',
    {
      method: 'POST',
      body: JSON.stringify({ variant_ids: ids }),
    }
  );
  if (res.error) throw new Error(res.error);
  return res.data?.results ?? [];
}

export type VendorSearchItem = {
  id: string;
  name: string;
  tags: string[] | null;
  product_count: number;
};

export type SupplierSearchItem = {
  id: string;
  name: string;
  email: string | null;
  tags: string[] | null;
  vendor_count: number;
};

type ResultsEnvelope<T> = { results: T[] };

async function searchResults<T>(
  path: string,
  q: string,
  limit: number,
  suffix = '',
  { throwOnError = false }: { throwOnError?: boolean } = {}
): Promise<T[]> {
  const trimmed = q.trim();
  if (!trimmed) return [];
  const res = await fetchApi<ResultsEnvelope<T>>(
    `${path}?q=${encodeURIComponent(trimmed)}&limit=${limit}${suffix}`
  );
  if (res.error && throwOnError) throw new Error(res.error);
  return res.data?.results ?? [];
}

export function searchProducts(
  q: string,
  limit: number,
  options?: { purchaseOrderItems?: boolean }
) {
  const suffix = options?.purchaseOrderItems ? '&purchaseOrderItems=true' : '';
  return searchResults<ProductSearchItem>('/products/search', q, limit, suffix);
}

/** searchProducts, but a failed search rejects, so "couldn't search" never reads as no matches. */
export function searchProductsOrThrow(q: string, limit: number) {
  return searchResults<ProductSearchItem>('/products/search', q, limit, '', { throwOnError: true });
}

/** Live product rows for label-queue refresh (name / price from DB). */
export async function fetchProductsByIds(
  variantIds: string[]
): Promise<ProductSearchItem[]> {
  const ids = [...new Set(variantIds.filter(Boolean))];
  if (ids.length === 0) return [];
  const res = await fetchApi<{ results: ProductSearchItem[] }>(
    `/products/by-ids?ids=${ids.map(encodeURIComponent).join(',')}`
  );
  if (res.error) throw new Error(res.error);
  return res.data?.results ?? [];
}

export function searchVendors(q: string, limit: number) {
  return searchResults<VendorSearchItem>('/vendors/search', q, limit);
}

export function searchSuppliers(q: string, limit: number) {
  return searchResults<SupplierSearchItem>('/suppliers/search', q, limit);
}

export type SavedFilterSearchItem = {
  id: string;
  pathname: string;
  name: string;
  queryString: string;
};

export function searchSavedFilters(q: string, limit: number) {
  return searchResults<SavedFilterSearchItem>('/saved-filters/search', q, limit);
}

export function searchPolicies(q: string, limit: number) {
  return searchResults<PolicySearchHit>('/policies/search', q, limit);
}

export function searchTasks(q: string, limit: number, options: { throwOnError?: boolean } = {}) {
  return searchResults<TaskSearchHit>('/tasks/search', q, limit, '', options);
}
